import React, { useMemo, useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import BaseCard from "../../../components/Cards/Card";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { toast, Toaster } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";
import Select from "react-select";
import countryList from "react-select-country-list";
import profiledone from "../../../assets/profiledone.svg";
const AddressLocation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const location = useLocation();
  const openedFromLocation = location?.state?.fromRegion === true;

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  const profileUpdateToken =
    useSelector((state) => state.auth.profileUpdateToken) ||
    localStorage.getItem("profileUpdateToken");




  const countryOptions = useMemo(() => {
    return countryList()
      .getData()
      .map((c) => ({
        value: c.label, // country name
        code: c.value,  // ISO code (alpha-2 label from lib, e.g. 'US')
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              src={`https://flagcdn.com/w40/${c.value.toLowerCase().slice(0, 2)}.png`}
              alt={c.label}
              style={{ width: 20, height: 15, objectFit: "contain" }}
            />
            {c.label}
          </div>
        ),
      }));
  }, []);

  // Default timezone from browser
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const showErr = (name) =>
    Boolean(formik.errors[name] && (formik.touched[name] || formik.submitCount > 0));
  const formik = useFormik({
    initialValues: {

      address: "",
      city: "",
      province: "",
      postalCode: "",
      country: "",
      countryCode: "",
      timezone: browserTz,

    },
    validate: (v) => {
      const e = {};
      const num = (x) => /^-?\d+(\.\d+)?$/.test(String(x || "").trim());

      if (!v.address?.trim()) e.address = "Address is required";
      if (!v.city?.trim()) e.city = "City is required";
      if (!v.province?.trim()) e.province = "Province/State is required";
      if (!v.postalCode?.trim()) e.postalCode = "Postal code is required";
      if (!v.country?.trim()) e.country = "Country is required";

      if (!v.countryCode?.trim()) {
        e.countryCode = "Country code is required";
      } else if (v.countryCode.length !== 3) {
        e.countryCode = "Country code must be exactly 3 characters";
      }

      if (!v.timezone?.trim()) e.timezone = "Timezone is required";


      return e;
    },
    onSubmit: async (values) => {
      setLoading(true);
      const url = `${BASE_URL}/account-executive/profile/location`;

      // Build payload exactly as your API expects
      const payload = {

        address: values.address,
        city: values.city,
        province: values.province,
        postalCode: values.postalCode,
        country: values.country,
        countryCode: values.countryCode, // e.g., "USA" (3-letter); if your select gives ISO-2, map it!
        timezone: values.timezone,

      };

      try {
        const res = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${profileUpdateToken}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        console.log("API response for location update:", data);
        if (!res.ok || data.error) throw new Error(data.message || "Failed to update location");

        toast.success("Location information updated successfully!");

        setTimeout(() => {
          if (openedFromLocation) {
            navigate("/face-scanning");
          } else {
            navigate("/registration-verification");
          }
        }, 400);
      } catch (err) {
        console.error("❌ API Error:", err);
        toast.error(err.message || "Failed to update location");
      } finally {
        setLoading(false);
      }
    },
  });

  // If you already know user's country from elsewhere, you can prefill:
  useEffect(() => {
    // Example: set country code mapping if empty
    if (!formik.values.countryCode && formik.values.country) {
      // If you need a 3-letter code (USA), map here; defaulting to ISO-2 upper
      // Replace with a map if your backend needs ISO-3.
      formik.setFieldValue("countryCode", formik.values.country.slice(0, 3).toUpperCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.country]);

  return (
    <Box sx={{ py: isMobile ? 5 : 8, px: 2, mt: isMobile ? 2 : 0 }}>
      <Toaster />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          alignItems: "center",
        }}
      >
        <BaseCard maxWidth={isMobile ? "100%" : isTablet ? "60%" : isLaptop ? "35%" : "35%"}>


          {/* Heading */}
          <CustomText
            sx={{
              ...textStyles.h4,
              color: theme.palette.tertiary.main,
              textAlign: "left",
              fontSize: "26px",
            }}
          >
            {t("addressLocation.title")}

          </CustomText>
          <CustomText
            sx={{
              ...textStyles.body5,
              color: theme.palette.text.primary,
              pt: 0.7,
              textAlign: "left",
            }}
          >
            {t("createProfileSubtitle")}

          </CustomText>

          {/* Form */}
          <form onSubmit={formik.handleSubmit}>
            {/* Lat / Lng */}




            <Box sx={{ display: "flex", gap: 1, mb: 1, mt: 2, }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.country")}</label>
                <Select
                  options={countryOptions}
                  value={
                    formik.values.country
                      ? countryOptions.find((o) => o.value === formik.values.country) || null
                      : null
                  }
                  onChange={(opt) => {
                    formik.setFieldValue("country", opt?.value || "");
                    // If your backend wants ISO-3 (e.g., "USA"), convert here as needed.
                    // For now we derive something; replace with your own ISO-3 map if necessary.
                    const iso2 = (opt?.code || "").toUpperCase();
                    // naive ISO-3 fallback: first 3 letters of name
                    const iso3Guess = (opt?.value || "").slice(0, 3).toUpperCase();
                    formik.setFieldValue("countryCode", iso3Guess);
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: 42,
                      // border: "2px solid rgba(9,30,66,0.14)",
                      border: showErr("address")
                        ? "1px solid red"
                        : "2px solid #091E4224",
                      borderRadius: "6px",
                      backgroundColor: "white",
                      fontSize: 14,
                      paddingLeft: 4,
                      fontFamily: "Poppins, sans-serif",
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: 14,
                      paddingTop: 6,
                      paddingBottom: 6,
                      backgroundColor: state.isSelected ? "#f0f8ff" : "white",
                      color: "#2C384C",
                      cursor: "pointer",
                    }),
                  }}
                />
                {formik.errors.country && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.country}</Box>
                )}
              </Box>


            </Box>
            {/* Address (full-width) */}
            <Box sx={{ mb: 2 }}>
              <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.address")}</label>
              <input
                className="project_input"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}

                style={{
                  width: "100%", height: 38, borderRadius: 6, marginTop: 7, fontSize: 14,
                  border: showErr("address")
                    ? "1px solid red"
                    : "2px solid #091E4224",
                }}
              />
              {formik.errors.address && (
                <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.address}</Box>
              )}
            </Box>

            {/* City + Province */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.city")}
                </label>
                <input
                  className="project_input"
                  name="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  style={{
                    width: "100%", height: 38, borderRadius: 6, marginTop: 7, fontSize: 14,
                    border: showErr("address")
                      ? "1px solid red"
                      : "2px solid #091E4224",
                  }}
                />
                {formik.errors.city && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.city}</Box>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.province")}</label>
                <input
                  className="project_input"
                  name="province"
                  value={formik.values.province}
                  onChange={formik.handleChange}
                  style={{
                    width: "100%", height: 38, borderRadius: 6, marginTop: 7, fontSize: 14,
                    border: showErr("address")
                      ? "1px solid red"
                      : "2px solid #091E4224",
                  }}
                />
                {formik.errors.province && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.province}</Box>
                )}
              </Box>
            </Box>

            {/* Postal Code */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.postalCode")}
                </label>
                <input
                  className="project_input"
                  name="postalCode"
                  value={formik.values.postalCode}
                  onChange={formik.handleChange}
                  style={{
                    width: "100%", height: 38, borderRadius: 6, marginTop: 7, fontSize: 14,
                    border: showErr("address")
                      ? "1px solid red"
                      : "2px solid #091E4224",
                  }}
                />
                {formik.errors.postalCode && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.postalCode}</Box>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.countryCode")}</label>
                <input
                  className="project_input"
                  name="countryCode"
                  value={formik.values.countryCode}
                  onChange={formik.handleChange}
                  placeholder="e.g., USA"
                  style={{
                    width: "100%", height: 38, borderRadius: 6, marginTop: 7, fontSize: 14,
                    border: showErr("address")
                      ? "1px solid red"
                      : "2px solid #091E4224",
                  }}
                />
                {formik.errors.countryCode && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.countryCode}</Box>
                )}
              </Box>
            </Box>





            {/* Timezone + Working Hours */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{}}>
                <label style={{ color: "#626F86", fontSize: "14px" }}>{t("addressLocation.timezone")}</label>
                <input
                  className="project_input"
                  name="timezone"
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  //   placeholder="e.g., America/New_York"
                  style={{ width: "100%", height: 38, borderRadius: 6, marginTop: 7, fontSize: 14 }}
                />
                {formik.errors.timezone && (
                  <Box sx={{ fontSize: 12, color: "red", mt: 0.5 }}>{formik.errors.timezone}</Box>
                )}
              </Box>

            </Box>


            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 4 }}>
              <CustomButton
                size='medium'
                label={t("back")}
                variant="text"
                onClick={() => navigate(-1)}
                sx={{
                  color: "black",
                  px: 6,
                  backgroundColor: "white",
                  // height: 20,
                  "&:hover": { backgroundColor: "#ffffff" },
                }}
              />
              <CustomButton
                size='medium'
                label={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    {loading && <CircularProgress size={18} sx={{ color: "#fff" }} />}
                    {t("addressLocation.next")}
                  </Box>
                }

                type="submit"
                sx={{ px: 6 }}
                disabled={loading}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default AddressLocation;


